import { CURATED_XRPL_ASSETS } from "./assets";
import { searchTokens } from "./catalog";

export interface TokenIdentity {
  currency: string;
  issuer: string;
  code: string;
}

function normalizedCurrency(currency: string): string {
  const normalized = currency.trim().toUpperCase();
  if (!/^[0-9A-F]{40}$/.test(normalized)) return normalized;

  try {
    const bytes = normalized.match(/.{2}/g) ?? [];
    const decoded = bytes
      .map((byte) => String.fromCharCode(Number.parseInt(byte, 16)))
      .join("")
      .replace(/\0+$/g, "")
      .trim()
      .toUpperCase();
    return decoded || normalized;
  } catch {
    return normalized;
  }
}

function sameAsset(
  left: Pick<TokenIdentity, "currency" | "issuer"> & { code?: string },
  right: Pick<TokenIdentity, "currency" | "issuer"> & { code?: string },
): boolean {
  if (left.issuer.trim() !== right.issuer.trim()) return false;

  const leftCodes = new Set(
    [left.currency, left.code].filter((value): value is string => Boolean(value)).map(normalizedCurrency),
  );
  const rightCodes = [right.currency, right.code]
    .filter((value): value is string => Boolean(value))
    .map(normalizedCurrency);
  return rightCodes.some((code) => leftCodes.has(code));
}

/** Returns bundled artwork for assets whose currency and issuer are curated. */
export function getBundledAssetLogo(asset: TokenIdentity): string | null {
  return CURATED_XRPL_ASSETS.find((candidate) => sameAsset(candidate, asset))?.logo ?? null;
}

/** Resolves registry artwork only when the result matches the exact trustline. */
export async function resolveAssetLogo(asset: TokenIdentity): Promise<string | null> {
  const bundled = getBundledAssetLogo(asset);
  if (bundled) return bundled;

  const matches = await searchTokens(asset.code);
  return matches.find((candidate) => sameAsset(candidate, asset))?.icon ?? null;
}